import { Box, FormControlLabel, Radio } from '@mui/material';
import { Button, TextField, Theme, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { ValueBoxDays } from '../ValueBoxDays';
import { useState } from 'react';
import { FrequencyDays } from '../../generated/graphql';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    width: '100%',
    padding: theme.spacing(2),
    justifyContent: 'center',
    border: '1px solid black',
    borderRadius: 10,
    flexDirection: 'column',
    '& > *': {
      margin: theme.spacing(0.25, 'auto', 0.25),
    },
    '& > *:last-child': {
      margin: theme.spacing(0, 'auto', 0),
    },
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    paddingLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  valueTypeOptions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
  },
  distribute: {
    justifyContent: 'space-between',
  },
  center: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& > *': {
      margin: theme.spacing(0.25, 0.25),
    },
  },
  end: {
    justifyContent: 'flex-end',
    '& > *': {
      margin: theme.spacing(0, 1),
    },
  },
  errorText: {
    fontSize: 11,
    color: 'red',
  },
}));

interface NewBoxInput {
  id?: number | undefined;
  locationId: number;
  boxName: string;
  frequency: FrequencyDays[];
  isFixedBox: boolean;
  isYesNoBox: boolean;
  isTimeBox: boolean;
  isAlphaNumeric: boolean;
  isAddedFromTerminal: boolean;
}

interface ValueBoxFormProps {
  initialValues: NewBoxInput;
  onSubmit: (values: NewBoxInput) => void;
  onCancel: () => void;
  isOpenFromTerminal: boolean;
}

export function AddValueBoxForm(props: ValueBoxFormProps) {
  const { initialValues, isOpenFromTerminal, onSubmit, onCancel } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const [showError, setShowError] = useState<boolean>(false);
  const [booleanBox, setBooleanBox] = useState<{
    isYesNoOnly: boolean;
    isTimeBox: boolean;
    isAlphaNumeric: boolean;
  }>({
    isYesNoOnly: initialValues.isYesNoBox,
    isTimeBox: initialValues.isTimeBox,
    isAlphaNumeric: initialValues.isAlphaNumeric,
  });
  const [currentBoxName, setCurrBoxName] = useState<string>(
    initialValues.boxName ? initialValues.boxName : '',
  );
  const [currentBoxFrequency, setCurrBoxFrequency] = useState<FrequencyDays[]>(
    initialValues.frequency ? initialValues.frequency : [],
  );

  return (
    <Box>
      <div className={classes.root}>
        <Box className={clsx(classes.row)}>
          <Typography style={{ marginRight: 12 }}>Name</Typography>
          <TextField
            id="boxName"
            name="boxName"
            style={{ minWidth: 380, marginRight: 12, marginTop: 1 }}
            placeholder={t('attributes.boxName')}
            value={currentBoxName}
            onChange={(e) => {
              setCurrBoxName(e.target.value);
              if (showError) setShowError(false);
            }}
          />
          <Typography style={{ marginRight: 12, marginTop: 8 }}>
            Type
          </Typography>
          <Box className={classes.valueTypeOptions}>
            <FormControlLabel
              label={<Typography style={{ fontSize: 12 }}>Numeric</Typography>}
              control={
                <Radio
                  name="valueType"
                  size="small"
                  disabled={initialValues.id !== undefined}
                  checked={
                    !booleanBox.isYesNoOnly &&
                    !booleanBox.isTimeBox &&
                    !booleanBox.isAlphaNumeric
                  }
                  onChange={() =>
                    setBooleanBox({
                      isYesNoOnly: false,
                      isTimeBox: false,
                      isAlphaNumeric: false,
                    })
                  }
                />
              }
            />
            <FormControlLabel
              label={<Typography style={{ fontSize: 12 }}>Yes/No</Typography>}
              control={
                <Radio
                  name="valueType"
                  size="small"
                  disabled={initialValues.id !== undefined}
                  checked={booleanBox.isYesNoOnly === true}
                  onChange={() =>
                    setBooleanBox({
                      isYesNoOnly: true,
                      isTimeBox: false,
                      isAlphaNumeric: false,
                    })
                  }
                />
              }
            />
          </Box>
          <Box className={classes.valueTypeOptions} style={{ marginLeft: 10 }}>
            <FormControlLabel
              label={
                <Typography style={{ fontSize: 12 }}>Alphanumeric</Typography>
              }
              control={
                <Radio
                  name="valueType"
                  size="small"
                  disabled={initialValues.id !== undefined}
                  checked={booleanBox.isAlphaNumeric === true}
                  onChange={() =>
                    setBooleanBox({
                      isYesNoOnly: false,
                      isTimeBox: false,
                      isAlphaNumeric: true,
                    })
                  }
                />
              }
            />
            <FormControlLabel
              label={<Typography style={{ fontSize: 12 }}> Time </Typography>}
              control={
                <Radio
                  name="valueType"
                  size="small"
                  disabled={initialValues.id !== undefined}
                  checked={booleanBox.isTimeBox === true}
                  onChange={() =>
                    setBooleanBox({
                      isYesNoOnly: false,
                      isTimeBox: true,
                      isAlphaNumeric: false,
                    })
                  }
                />
              }
            />
          </Box>
        </Box>
        {!isOpenFromTerminal && (
          <Box className={clsx(classes.row)}>
            <Typography style={{ marginRight: 12 }}>Frequency</Typography>
            <Box ml={2}>
              <ValueBoxDays
                isReadOnly={false}
                daysArray={currentBoxFrequency}
                onDaysChanged={(newFrequency) => {
                  setCurrBoxFrequency(newFrequency);
                  if (showError) setShowError(false);
                }}
              />
            </Box>
          </Box>
        )}
        {showError && currentBoxName === '' && (
          <Typography
            className={classes.errorText}
          >{`Box name is required!`}</Typography>
        )}
        {showError &&
          currentBoxFrequency.length === 0 &&
          !isOpenFromTerminal && (
            <Typography
              className={classes.errorText}
            >{`Choose at least one day!`}</Typography>
          )}
        <Box
          className={clsx(classes.row, classes.center)}
          style={{ marginTop: 30 }}
        >
          <Button
            variant="contained"
            onClick={() => {
              //validate
              if (
                currentBoxName === '' ||
                (currentBoxFrequency.length === 0 && !isOpenFromTerminal)
              ) {
                setShowError(true);
              } else {
                onSubmit({
                  id: initialValues.id,
                  boxName: currentBoxName,
                  locationId: initialValues.locationId,
                  frequency: currentBoxFrequency,
                  isFixedBox: false,
                  isYesNoBox: booleanBox.isYesNoOnly,
                  isAddedFromTerminal: isOpenFromTerminal,
                  isTimeBox: booleanBox.isTimeBox,
                  isAlphaNumeric: booleanBox.isAlphaNumeric === true,
                });
              }
            }}
          >
            {initialValues.id
              ? t('actions.update')
              : t('actions.create.capitalized')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              onCancel();
            }}
          >
            {t('actions.cancel')}
          </Button>
        </Box>
      </div>
    </Box>
  );
}
